/**
 * The Notifications switch in settings (D310): what the phone can do, and turning web push
 * on and off. The server decides what is sent and when; this only subscribes.
 */
import { apiRequest } from '../game/sync.ts';
import { cap, t as term } from './terms.ts';

/** What stands between the player and turning notifications on, or null when nothing does. */
export type PushBlock = 'unsupported' | 'ios_home_screen' | 'denied' | 'off_on_server';

/** The words for each, from STYLE.md: plain, and they say what to do. `ios_home_screen`'s
 * steps are the same as the install prompt's (D332); `NotificationsSetting` adds a link to
 * show them again rather than repeating a second copy here. */
export const PUSH_BLOCK_WORDS: Record<PushBlock, string> = {
  unsupported: 'This browser cannot show notifications.',
  ios_home_screen: 'On iPhone, add Idle Worlds to your home screen first. Then open it from there.',
  denied: 'Notifications are blocked. Turn them on for Idle Worlds in your phone’s settings.',
  off_on_server: 'Notifications are not available yet.',
};

/** The line under the switch: what it sends. */
export const PUSH_ABOUT = `${cap(term('barn'))} full, ${term('festival')} ready, new orders. Never at night.`;

export interface PushEnv {
  userAgent: string;
  maxTouchPoints: number;
  /** Opened from the home screen rather than in the browser. */
  standalone: boolean;
  /** Has a service worker, PushManager and Notification. */
  supported: boolean;
  permission: 'default' | 'granted' | 'denied';
}

/** An iPhone or iPad, including an iPad that says it is a Mac. */
export function isIos(userAgent: string, maxTouchPoints: number): boolean {
  if (/iPhone|iPad|iPod/.test(userAgent)) return true;
  return /Macintosh/.test(userAgent) && maxTouchPoints > 1;
}

/**
 * Why notifications cannot be turned on here. On iPhone web push only exists for an app
 * added to the home screen, so that is said before "this browser cannot".
 */
export function pushBlock(env: PushEnv): PushBlock | null {
  if (isIos(env.userAgent, env.maxTouchPoints) && !env.standalone) return 'ios_home_screen';
  if (!env.supported) return 'unsupported';
  if (env.permission === 'denied') return 'denied';
  return null;
}

/** The browser's own answers, read now. */
export function readPushEnv(): PushEnv {
  const nav = typeof navigator === 'undefined' ? undefined : navigator;
  const win = typeof window === 'undefined' ? undefined : window;
  const supported =
    !!nav && 'serviceWorker' in nav && !!win && 'PushManager' in win && 'Notification' in win;
  const standalone =
    !!win &&
    ((typeof win.matchMedia === 'function' &&
      win.matchMedia('(display-mode: standalone)').matches) ||
      (nav as (Navigator & { standalone?: boolean }) | undefined)?.standalone === true);
  return {
    userAgent: nav?.userAgent ?? '',
    maxTouchPoints: nav?.maxTouchPoints ?? 0,
    standalone,
    supported,
    permission: supported ? Notification.permission : 'default',
  };
}

/** The base64url VAPID key as the bytes `pushManager.subscribe` wants. */
export function keyBytes(b64u: string): Uint8Array<ArrayBuffer> {
  const b64 = b64u.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function timeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

async function tellServer(sub: PushSubscription): Promise<boolean> {
  const res = await apiRequest('/push/subscribe', {
    method: 'POST',
    body: JSON.stringify({ ...sub.toJSON(), timeZone: timeZone() }),
  });
  return !!res?.ok;
}

/** Whether this browser has a push subscription now. Sends it again, so the zone stays current. */
export async function pushIsOn(): Promise<boolean> {
  if (!readPushEnv().supported || Notification.permission !== 'granted') return false;
  const sub = await (await registration())?.pushManager.getSubscription();
  if (!sub) return false;
  await tellServer(sub);
  return true;
}

/** The server's public key, or null when push is off there (no VAPID keys) or it cannot be asked. */
export async function pushKey(): Promise<string | null> {
  const res = await apiRequest('/push/key');
  if (!res?.ok) return null;
  const body = (await res.json().catch(() => null)) as {
    available?: boolean;
    publicKey?: string;
  } | null;
  return body?.available && body.publicKey ? body.publicKey : null;
}

/** Asks permission and subscribes. Returns null when on, else what stopped it. */
export async function turnPushOn(): Promise<PushBlock | null> {
  const env = readPushEnv();
  const block = pushBlock(env);
  if (block) return block;
  const publicKey = await pushKey();
  if (!publicKey) return 'off_on_server';
  if ((await Notification.requestPermission()) !== 'granted') return 'denied';
  const reg = await registration();
  if (!reg) return 'unsupported';
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: keyBytes(publicKey),
  });
  return (await tellServer(sub)) ? null : 'off_on_server';
}

/** Unsubscribes here and tells the server to forget it. */
export async function turnPushOff(): Promise<void> {
  const sub = await (await registration())?.pushManager.getSubscription();
  if (!sub) return;
  await apiRequest('/push/unsubscribe', {
    method: 'POST',
    body: JSON.stringify({ endpoint: sub.endpoint }),
  });
  await sub.unsubscribe();
}
